// Web Push subscription manager.
//
// Three concepts in play:
//   1. Service worker registration — required to receive pushes. Persists
//      across reloads.
//   2. PushManager subscription — the per-browser subscription object. We
//      register it with the OS push service and forward the result to
//      our backend so the server knows where to send notifications.
//   3. Notification permission — "Allow / Block / Default" from the user.
//
// This hook unifies all three into a simple state machine for the UI.

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "@/api/client";

export type PushState =
  | "unsupported" // browser has no PushManager / Notification API
  | "blocked" // user declined permission
  | "default" // permission not asked yet
  | "subscribed" // we have an active subscription
  | "loading"
  | "error";

export interface PushHook {
  state: PushState;
  error?: string;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  sendTestEnabled: boolean;
}

// Browsers expect the VAPID public key as a Uint8Array, not the base64url
// string our backend stores. Decode it once when subscribing.
function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

async function ensureRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  // Register if not already; getRegistration if it is.
  let reg = await navigator.serviceWorker.getRegistration("/sw.js");
  if (!reg) {
    reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  }
  await navigator.serviceWorker.ready;
  return reg;
}

export function usePushSubscription(): PushHook {
  const [state, setState] = useState<PushState>("loading");
  const [error, setError] = useState<string | undefined>();

  const refresh = useCallback(async () => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setState("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setState("blocked");
      return;
    }
    const reg = await ensureRegistration();
    if (!reg) {
      setState("unsupported");
      return;
    }
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      setState("subscribed");
    } else if (Notification.permission === "granted") {
      // Permission was granted earlier (maybe on a different device session)
      // but no active subscription on this device. Treat as "default" — user
      // can re-subscribe with one click.
      setState("default");
    } else {
      setState("default");
    }
  }, []);

  useEffect(() => {
    refresh().catch((e) => {
      setState("error");
      setError((e as Error).message);
    });
  }, [refresh]);

  const subscribe = useCallback(async () => {
    setError(undefined);
    setState("loading");
    try {
      // 1. Permission. Has to be triggered from a user gesture, which it is —
      // this hook's `subscribe()` is wired to a button onClick.
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState(permission === "denied" ? "blocked" : "default");
        return;
      }

      // 2. SW registration
      const reg = await ensureRegistration();
      if (!reg) {
        setState("unsupported");
        return;
      }

      // 3. Pull the VAPID public key from our server (no auth needed)
      const { publicKey } = await api.push.vapidPublicKey();
      if (!publicKey) {
        setError("VAPID-Schlüssel auf dem Server nicht konfiguriert.");
        setState("error");
        return;
      }

      // 4. PushManager subscription
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        // Cast: TS lib types want `BufferSource` but reject Uint8Array from a
        // potentially-SharedArrayBuffer-backed input. At runtime we always
        // build a fresh ArrayBuffer-backed Uint8Array, so it's safe.
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
        });
      }

      // 5. Forward to backend so it knows where to send pushes
      const json = sub.toJSON();
      await api.push.subscribe({
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh ?? "",
        auth: json.keys?.auth ?? "",
      });
      setState("subscribed");
    } catch (e) {
      setState("error");
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setState("loading");
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await api.push.unsubscribe(sub.endpoint).catch(() => {});
        await sub.unsubscribe();
      }
      setState("default");
    } catch (e) {
      setState("error");
      setError((e as Error).message);
    }
  }, []);

  return {
    state,
    error,
    subscribe,
    unsubscribe,
    sendTestEnabled: state === "subscribed",
  };
}
